import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Tag,
  Upload,
} from "antd";
import { useAuth } from "../../../../../data/hooks/firebase/useAuth";
import { doc, updateDoc } from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { DELIVERY_DAYS } from "../../../../../constants/OrderConstant";
import { useCountries } from "../../../../../data/hooks/firestore/useCountries";
import TextArea from "antd/es/input/TextArea";
import { Option } from "antd/es/mentions";
import { CURRENCY, DEFAULT_DELIVERY_COST, FREE_DELIVERY_FROM_PRICE } from "../../../../../constants/DeliveryConstants";
import { FIRESTORE_CUSTOMERS_TABLE } from "../../../../../constants/FirestoreConstant";
import { firestore, storage } from "../../../../../configs/FirebaseConfig";
import dayjs from "dayjs";
import { getFirebaseIDToken } from "../../../../../functions/firebase/firebase";
import { updateExactAccount } from "../../../../../functions/api/exact";
import { showOperationSummary } from "../../../../../utils/operationSummary";
import { PRODUCT_STOCK_COUNTRIES } from "../../../../../constants/ProductConstant";
import i18n from "../../../../../translations/i18n";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import {
  findDeliveryZoneByPostalCode,
  getDeliveryZoneStatus,
  sanitizePostalCode,
} from "../../../../../functions/deliveryZones";

export default function EditDeliveryDetailsModal({ company, visible, onClose }) {
  const { t } = useTranslation();
  const [deliveryDetailsForm] = Form.useForm();
  const { email } = useAuth();
  const { getCountryDeliveryLabels, getCountryDelivery } = useCountries();
  const watchedCountry = Form.useWatch("country", deliveryDetailsForm);
  const deliveryLabelValue = Form.useWatch("deliveryLabel", deliveryDetailsForm);
  const zipCodeValue = Form.useWatch("zipCode", deliveryDetailsForm);
  const [isLoading, setIsLoading] = useState(false);
  const [deliveryZone, setDeliveryZone] = useState(null);
  const [deliveryInstructionsFile, setDeliveryInstructionsFile] = useState(null);
  const [currentDeliveryInstructionsUrl, setCurrentDeliveryInstructionsUrl] = useState(null);
  const [currentDeliveryInstructionsPath, setCurrentDeliveryInstructionsPath] = useState(null);
  const [showAutoUpdateWarning, setShowAutoUpdateWarning] = useState(false);
  const [initialDeliveryLabel, setInitialDeliveryLabel] = useState(null);

  useEffect(() => {
    if (company && visible) {
      deliveryDetailsForm.setFieldsValue({
        deliveryCost: company.deliveryCost,
        deliveryCostThreshold: company.deliveryCostThreshold,
        customerDeliveryDays: company.customerDeliveryDays
          ? company.customerDeliveryDays.split(",").map(Number)
          : DELIVERY_DAYS.split(",").map(Number),
        deliveryNotes: company.deliveryNotes,
        deliveryLabel: company.deliveryLabel,
        deliveryTimeSlot: company.deliveryTimeSlot ? JSON.stringify(company.deliveryTimeSlot) : null,
        street: company.street,
        houseNumber: company.houseNumber,
        houseNumberAddition: company.houseNumberAddition,
        zipCode: company.zipCode,
        city: company.city,
        country: company.country,
      });
      setDeliveryInstructionsFile(null);
      setCurrentDeliveryInstructionsUrl(company?.deliveryInstructionsUrl || null);
      setCurrentDeliveryInstructionsPath(company?.deliveryInstructionsPath || null);
      setInitialDeliveryLabel(company.deliveryLabel);
      setShowAutoUpdateWarning(false);

      // Auto-detect delivery zone for French customers
      if (company.country === "FR" && company.zipCode) {
        const sanitized = sanitizePostalCode(company.zipCode);
        const countryConfig = getCountryDelivery(company.country);
        const zone = findDeliveryZoneByPostalCode(sanitized, countryConfig);
        setDeliveryZone(zone);
      }
    } else {
      deliveryDetailsForm.resetFields();
      setDeliveryInstructionsFile(null);
      setCurrentDeliveryInstructionsUrl(null);
      setCurrentDeliveryInstructionsPath(null);
      setInitialDeliveryLabel(null);
      setShowAutoUpdateWarning(false);
      setDeliveryZone(null);
    }
  }, [company, visible]);

  // Auto-detect delivery zone for French postal codes when zipCode or country changes
  useEffect(() => {
    if (watchedCountry === "FR" && zipCodeValue) {
      const sanitized = sanitizePostalCode(zipCodeValue);
      const countryConfig = getCountryDelivery(watchedCountry);
      const zone = findDeliveryZoneByPostalCode(sanitized, countryConfig);
      setDeliveryZone(zone);
    } else {
      setDeliveryZone(null);
    }
  }, [zipCodeValue, watchedCountry, getCountryDelivery]);

  // Auto-populate delivery days and time slot when delivery label changes
  useEffect(() => {
    if (!deliveryLabelValue || !watchedCountry) return;

    const deliverySettings = getCountryDelivery(watchedCountry);
    if (!deliverySettings?.deliveryLabels) return;

    const labelConfig = deliverySettings.deliveryLabels.find((dl) => dl.deliveryLabel === deliveryLabelValue);

    if (labelConfig) {
      // Check if label has changed from initial value
      const hasLabelChanged = initialDeliveryLabel && deliveryLabelValue !== initialDeliveryLabel;

      // Always update delivery days when label changes
      if (labelConfig.deliveryDays) {
        deliveryDetailsForm.setFieldValue("customerDeliveryDays", labelConfig.deliveryDays);
      }

      // Always update time slot when label changes
      if (labelConfig.deliveryTimeSlots?.length > 0) {
        deliveryDetailsForm.setFieldValue("deliveryTimeSlot", JSON.stringify(labelConfig.deliveryTimeSlots[0]));
      }

      // Show warning if label was changed by user
      if (hasLabelChanged) {
        setShowAutoUpdateWarning(true);
      }
    }
  }, [deliveryLabelValue, watchedCountry, getCountryDelivery, deliveryDetailsForm, initialDeliveryLabel]);

  const handleSaveDeliveryDetails = () => {
    deliveryDetailsForm.validateFields().then(async (values) => {
      setIsLoading(true);

      // Initialize operation results
      const operationResults = {
        firestore: { attempted: true, success: false, error: null },
        exact: { attempted: false, success: false, error: null },
      };

      const address =
        values.street +
        " " +
        values.houseNumber +
        (values.houseNumberAddition ? " " + values.houseNumberAddition : "") +
        ", " +
        values.city +
        ", " +
        values.zipCode;

      const obj = {
        deliveryCost: values.deliveryCost,
        deliveryCostThreshold: values.deliveryCostThreshold,
        customerDeliveryDays: Array.isArray(values.customerDeliveryDays)
          ? values.customerDeliveryDays.join(",")
          : values.customerDeliveryDays || DELIVERY_DAYS,
        deliveryNotes: values.deliveryNotes || null,
        deliveryLabel: values.deliveryLabel || null,
        deliveryTimeSlot: values.deliveryTimeSlot ? JSON.parse(values.deliveryTimeSlot) : null,
        customerAddress: address,
        street: values.street,
        houseNumber: values.houseNumber,
        houseNumberAddition: values.houseNumberAddition || null,
        zipCode: values.zipCode,
        city: values.city,
        country: values.country,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      };

      try {
        let deliveryInstructionsUrl = currentDeliveryInstructionsUrl || null;
        let deliveryInstructionsPath = currentDeliveryInstructionsPath || null;

        if (deliveryInstructionsFile && company?.id) {
          const storagePath = `delivery-instructions/${company.id}/delivery-instructions.pdf`;
          const storageRef = ref(storage, storagePath);

          try {
            await uploadBytes(storageRef, deliveryInstructionsFile);
            const downloadUrl = await getDownloadURL(storageRef);
            deliveryInstructionsUrl = downloadUrl;
            deliveryInstructionsPath = storagePath;

            if (currentDeliveryInstructionsPath && currentDeliveryInstructionsPath !== storagePath) {
              deleteObject(ref(storage, currentDeliveryInstructionsPath)).catch(console.error);
            }
          } catch (uploadError) {
            console.error("Delivery Instructions upload error:", uploadError);
            message.error("Upload failed: Delivery Instructions");
            setIsLoading(false);
            return;
          }
        }

        const objWithDeliveryInstructions = {
          ...obj,
          deliveryInstructionsUrl,
          deliveryInstructionsPath,
        };

        // Update Firestore first
        await updateDoc(doc(firestore, FIRESTORE_CUSTOMERS_TABLE, company.id), objWithDeliveryInstructions);

        operationResults.firestore.success = true;

        // If customer has exactId, also update Exact
        if (company.exactId) {
          operationResults.exact.attempted = true;
          try {
            const token = await getFirebaseIDToken();
            const exactUpdateObj = {
              exactAccountId: company.exactId,
              customerName: company.customerName,
              street: values.street,
              houseNumber: values.houseNumber,
              houseNumberAddition: values.houseNumberAddition,
              city: values.city,
              zipCode: values.zipCode,
              country: values.country,
              customerDeliveryDays: Array.isArray(values.customerDeliveryDays)
                ? values.customerDeliveryDays.join(",")
                : values.customerDeliveryDays || DELIVERY_DAYS,
            };

            await updateExactAccount(exactUpdateObj, token);
            operationResults.exact.success = true;
          } catch (exactError) {
            console.error("Exact update error:", exactError);
            // Extract the most detailed error message available
            const detailedError =
              exactError.response?.data?.Message || exactError.response?.data?.message || exactError.message;
            operationResults.exact.error = detailedError;
          }
        }

        setIsLoading(false);
        onClose();
        showOperationSummary(operationResults, t);
      } catch (firestoreError) {
        console.error("Firestore error:", firestoreError);
        operationResults.firestore.error = firestoreError.message;
        setIsLoading(false);
        showOperationSummary(operationResults, t);
      }
    });
  };

  return (
    <Modal
      title={t("edit_delivery_details_value", { company: !!company ? company.customerName : "" })}
      open={visible}
      onOk={handleSaveDeliveryDetails}
      onCancel={onClose}
      okText={t("save")}
      cancelText={t("close")}
      okButtonProps={{ loading: isLoading, htmlType: "submit" }}
      zIndex={1002}
      centered
      width={800}
    >
      <Form form={deliveryDetailsForm} layout="vertical">
        <Row gutter={16}>
          <Col lg={12}>
            <Form.Item name={"deliveryCost"} label={t("delivery_cost")} initialValue={DEFAULT_DELIVERY_COST}>
              <InputNumber
                placeholder={t("delivery_cost")}
                style={{ width: "100%" }}
                min={0}
                precision={2}
                step={0.05}
                formatter={(value) => `${CURRENCY} ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                parser={(value) => {
                  const parsedValue = value.replace(new RegExp(`${CURRENCY}\\s?|(,*)`, "g"), "");
                  return parsedValue === "" ? 0 : Number(parsedValue);
                }}
              />
            </Form.Item>
          </Col>
          <Col lg={12}>
            <Form.Item
              name={"deliveryCostThreshold"}
              label={t("delivery_cost_threshold")}
              initialValue={FREE_DELIVERY_FROM_PRICE}
            >
              <InputNumber
                placeholder={t("delivery_cost_threshold")}
                style={{ width: "100%" }}
                precision={2}
                step={0.5}
                min={0}
                formatter={(value) => `${CURRENCY} ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                parser={(value) => {
                  const parsedValue = value.replace(new RegExp(`${CURRENCY}\\s?|(,*)`, "g"), "");
                  return parsedValue === "" ? 0 : Number(parsedValue);
                }}
              />
            </Form.Item>
          </Col>
          <Col lg={12}>
            <Form.Item name="deliveryLabel" label={t("delivery_label")}>
              <Select style={{ width: "100%" }} placeholder={t("delivery_label_placeholder")}>
                {getCountryDeliveryLabels(watchedCountry || company?.country).map((deliveryLabel) => (
                  <Option key={deliveryLabel.code} value={deliveryLabel.code}>
                    {deliveryLabel.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col lg={12}>
            <Form.Item
              name="deliveryTimeSlot"
              label={t("delivery_time_slot")}
              rules={[
                {
                  required: true,
                  message: t("form.select_delivery_time_slot"),
                },
              ]}
            >
              <Select
                style={{ width: "100%" }}
                placeholder={t("select_delivery_time_slot")}
                disabled={!deliveryLabelValue}
              >
                {deliveryLabelValue &&
                  getCountryDelivery(watchedCountry || company?.country)
                    ?.deliveryLabels?.find((dl) => dl.deliveryLabel === deliveryLabelValue)
                    ?.deliveryTimeSlots?.map((slot, index) => (
                      <Select.Option key={index} value={JSON.stringify(slot)}>
                        {slot.start} - {slot.end}
                        {slot.deliverySurcharge > 0 && ` (+€${slot.deliverySurcharge})`}
                      </Select.Option>
                    ))}
              </Select>
            </Form.Item>
            {showAutoUpdateWarning && (
              <Alert
                message={t("auto_updated_based_on_delivery_label")}
                type="warning"
                showIcon
                closable
                style={{ marginTop: -16, marginBottom: 16 }}
              />
            )}
          </Col>
          <Col lg={12}>
            <Form.Item
              name={"customerDeliveryDays"}
              label={t("delivery_days")}
              initialValue={DELIVERY_DAYS.split(",").map(Number)}
            >
              <Select mode="multiple" placeholder={t("delivery_days")} allowClear style={{ width: "100%" }}>
                <Select.Option value={1}>{t("week_days.monday")}</Select.Option>
                <Select.Option value={2}>{t("week_days.tuesday")}</Select.Option>
                <Select.Option value={3}>{t("week_days.wednesday")}</Select.Option>
                <Select.Option value={4}>{t("week_days.thursday")}</Select.Option>
                <Select.Option value={5}>{t("week_days.friday")}</Select.Option>
                <Select.Option value={6}>{t("week_days.saturday")}</Select.Option>
                <Select.Option value={0}>{t("week_days.sunday")}</Select.Option>
              </Select>
            </Form.Item>
            {showAutoUpdateWarning && (
              <Alert
                message={t("auto_updated_based_on_delivery_label")}
                type="warning"
                showIcon
                closable
                style={{ marginTop: -16, marginBottom: 16 }}
              />
            )}
          </Col>
          <Col xs={24}>
            <Form.Item name={"deliveryNotes"} label={t("delivery_notes")}>
              <TextArea placeholder={t("delivery_notes_placeholder")} />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item label="Delivery Instructions">
              <Space direction="vertical" style={{ width: "100%" }}>
                {!!currentDeliveryInstructionsUrl && (
                  <a href={currentDeliveryInstructionsUrl} target="_blank" rel="noreferrer">
                    Delivery Instructions (PDF)
                  </a>
                )}
                {!currentDeliveryInstructionsUrl && (
                  <Upload
                    accept="application/pdf"
                    maxCount={1}
                    showUploadList={{ showRemoveIcon: false }}
                    beforeUpload={(file) => {
                      const isPdf = file.type === "application/pdf" || file.name?.toLowerCase().endsWith(".pdf");
                      if (!isPdf) {
                        message.error("Only PDF files are allowed");
                        return Upload.LIST_IGNORE;
                      }
                      setDeliveryInstructionsFile(file);
                      return false;
                    }}
                  >
                    <Button>Upload PDF</Button>
                  </Upload>
                )}
                {!!currentDeliveryInstructionsUrl && (
                  <Popconfirm
                    title="Remove Delivery Instructions?"
                    okText={t("delete")}
                    cancelText={t("close")}
                    onConfirm={async () => {
                      if (!company?.id) return;
                      try {
                        if (currentDeliveryInstructionsPath) {
                          await deleteObject(ref(storage, currentDeliveryInstructionsPath));
                        } else if (currentDeliveryInstructionsUrl) {
                          await deleteObject(ref(storage, currentDeliveryInstructionsUrl));
                        }
                      } catch (e) {
                        console.error(e);
                      }

                      try {
                        await updateDoc(doc(firestore, FIRESTORE_CUSTOMERS_TABLE, company.id), {
                          deliveryInstructionsUrl: null,
                          deliveryInstructionsPath: null,
                          updatedAt: dayjs().unix(),
                          updatedBy: email,
                        });
                        setCurrentDeliveryInstructionsUrl(null);
                        setCurrentDeliveryInstructionsPath(null);
                        setDeliveryInstructionsFile(null);
                        message.success("Delivery Instructions removed");
                      } catch (e) {
                        console.error(e);
                        message.error("Failed to remove Delivery Instructions");
                      }
                    }}
                  >
                    <Button danger>Remove</Button>
                  </Popconfirm>
                )}
              </Space>
            </Form.Item>
          </Col>
        </Row>
        <Divider style={{ margin: "0 0 12px 0" }} />
        <Row gutter={16}>
          <Col xs={24}>
            <h4>{t("delivery_address")}</h4>
          </Col>
          <Col xs={24}>
            <Form.Item
              name={"street"}
              label={t("street")}
              rules={[
                {
                  required: true,
                  message: t("form.enter_street"),
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item
              name="zipCode"
              label={
                <span>
                  {t("zip_code")}
                  {watchedCountry === "FR" && deliveryZone && (
                    <Tag color={getDeliveryZoneStatus(deliveryZone).color} style={{ marginLeft: 8 }}>
                      {deliveryZone.deliveryTime}
                    </Tag>
                  )}
                  {watchedCountry === "FR" && zipCodeValue && !deliveryZone && (
                    <Tag color="orange" style={{ marginLeft: 8 }}>
                      {t("delivery_zone_unknown")}
                    </Tag>
                  )}
                </span>
              }
              rules={[
                {
                  required: true,
                  message: t("form.enter_zip_code"),
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={12} lg={6}>
            <Form.Item
              name="houseNumber"
              label={t("house_number")}
              rules={[
                {
                  required: true,
                  message: t("form.enter_house_number"),
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={12} lg={6}>
            <Form.Item name="houseNumberAddition" label={t("addition")}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item
              name="city"
              label={t("city")}
              rules={[
                {
                  required: true,
                  message: t("form.enter_city"),
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item name="country" label={t("country")}>
              <Select className="mr-2">
                {PRODUCT_STOCK_COUNTRIES.map((country) => (
                  <Select.Option key={country.code} value={country.code}>
                    {i18n.t(country.label)}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
